import { MenuItem } from "@kaltura/ds-react-components";
import { useState } from "react";
import { ButtonClickAnalyticsType, FilterValues } from "@kaltura/mediaspace-shared-types";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { UNFILED_FOLDER_ID } from "../types";
import { RemoveFromCurrentFolderModal, RemoveFromCurrentFolderModalProps } from "./RemoveFromCurrentFolderModal";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface RemoveFromCurrentFolderProps extends RemoveFromCurrentFolderModalProps {
    /** currently selected folder id in the search filters
     * to remove entries from
     */
    selectedFolderId?: string;
    /** currently selected filter values */
    filterValues?: FilterValues;
    /** button name for reporting menu item click analytics  */
    buttonAnalyticsActionName?: string;
}

/**
 * menu item to remove media from currently
 * selected folder in the filters
 * used in:
 * my-media single entry action, my-media bulk action
 */
export function RemoveFromCurrentFolder({
    autoFocus,
    onCloseMenu,
    reloadMedia,
    entryIds,
    selectedFolderId,
    filterValues,
    buttonAnalyticsActionName,
    ...menuItemProps
}: RemoveFromCurrentFolderProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const sendButtonAnalytics = useButtonAnalytics();

    // current folder id is derived from:
    // selectedFolderId prop - for single entry action, coming from the server
    // filterValues.folderId - for bulk entries action, coming from the my-media app
    const currentFolderId =
        selectedFolderId ?? (filterValues && typeof filterValues.folderId === "string" ? filterValues.folderId : "");

    if (!currentFolderId || currentFolderId === UNFILED_FOLDER_ID) return null;

    const handleOpenModal = () => {
        setIsModalOpen(true);
        buttonAnalyticsActionName
            && sendButtonAnalytics(
                buttonAnalyticsActionName,
                ButtonClickAnalyticsType.DELETE,
                entryIds.length === 1 ? entryIds[0] : undefined,
                "folders"
            );
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleClose = () => {
        handleCloseModal();
        onCloseMenu?.();
    };

    return (
        <>
            <MenuItem key={"remove-from-current-folder"} onClick={handleOpenModal} {...menuItemProps}>
                {translate("Remove from this folder")}
            </MenuItem>
            <ButtonAnalyticsProvider location={"Modal - Remove from folders"} applicationFeature={"folders"}>
                <RemoveFromCurrentFolderModal
                    isOpen={isModalOpen}
                    entryIds={entryIds}
                    currentFolderId={currentFolderId}
                    onClose={handleCloseModal}
                    onCloseMenu={handleClose}
                    reloadMedia={reloadMedia}
                />
            </ButtonAnalyticsProvider>
        </>
    );
}

export default RemoveFromCurrentFolder;
